import { Link } from 'react-router'
import type { LessonSummary, UnitWithLessons } from '@shared/types/catalog'
import { useCourse, useCourses } from '@web/lib/catalog'
import { useLatestPlacement } from '@web/lib/placement'

/**
 * The course map: every unit as a vertical path, with lock and completion
 * state. The countdown and today's mission live on the dashboard; this screen
 * answers "what else is there?".
 */
export function CoursePage() {
  const { data: courses, isPending: coursesPending } = useCourses()
  const courseId = courses?.[0]?.id
  const { data: course, isPending: coursePending } = useCourse(courseId ?? '')
  const placement = useLatestPlacement()

  if (coursesPending || (courseId && coursePending)) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span
          aria-label="Loading"
          className="size-8 animate-spin rounded-full border-3 border-brand-600 border-t-transparent"
        />
      </div>
    )
  }

  return (
    <main className="mx-auto min-h-dvh max-w-lg px-4 pt-[max(1.5rem,var(--safe-top))] pb-[max(2rem,var(--safe-bottom))]">
      <header className="mb-6">
        <h1 className="font-display text-3xl font-bold text-ink-900">Lessons</h1>
        {course && (
          <p className="mt-1 text-ink-500">
            {course.lessonsCompleted} of {course.lessonCount} complete
          </p>
        )}
      </header>


      {/*
        Offered only before she has taken it and only while she has barely
        started. Once she is genuinely under way, a "skip ahead" prompt is
        noise at best and a temptation to over-skip at worst.
      */}
      {!placement.isPending && !placement.data && (course?.lessonsCompleted ?? 0) === 0 && (
        <Link
          to="/placement"
          className="mb-4 block rounded-card border-l-4 border-gold-500 bg-cream p-4 ring-1 ring-brand-900/5"
        >
          <p className="font-display text-lg font-bold text-ink-900">Already know some Portuguese?</p>
          <p className="mt-0.5 text-sm text-ink-700">
            Take a five-minute placement test and skip what you already know.
          </p>
        </Link>
      )}

      {course?.nextLessonId && (
        <Link
          to={`/lesson/${course.nextLessonId}`}
          className="mb-6 flex min-h-13 items-center justify-center rounded-xl bg-brand-600 px-5 font-semibold text-white"
        >
          {course.lessonsCompleted === 0 ? 'Start learning' : 'Continue'}
        </Link>
      )}

      {!course && (
        <p className="rounded-card bg-white p-5 text-ink-700 ring-1 ring-brand-900/5">
          No course yet. Run the content seed to load “Portuguese Before Brazil”.
        </p>
      )}

      <div className="space-y-6">
        {course?.units.map((unit) => (
          <UnitSection key={unit.id} unit={unit} />
        ))}
      </div>
    </main>
  )
}

function UnitSection({ unit }: { unit: UnitWithLessons }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex size-11 items-center justify-center rounded-full text-xl"
          style={{ backgroundColor: `${unit.themeColor ?? '#22c55e'}20` }}
        >
          {unit.icon ?? '📘'}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold text-ink-900">{unit.title}</h2>
          <p className="text-sm text-ink-500">
            {unit.lessonsCompleted}/{unit.lessons.length} lessons
          </p>
        </div>
      </div>

      <ul className="space-y-2">
        {unit.lessons.map((lesson) => (
          <li key={lesson.id}>
            <LessonRow lesson={lesson} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function LessonRow({ lesson }: { lesson: LessonSummary }) {
  const locked = lesson.status === 'locked'

  const body = (
    <>
      <span
        aria-hidden="true"
        className={`flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          lesson.status === 'completed'
            ? 'bg-green-100 text-green-700'
            : locked
              ? 'bg-sand-100 text-ink-400'
              : 'bg-brand-100 text-brand-700'
        }`}
      >
        {lesson.status === 'completed' ? '✓' : locked ? '🔒' : lesson.position}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-ink-900">{lesson.title}</span>
        {lesson.objective && (
          <span className="block truncate text-sm text-ink-500">{lesson.objective}</span>
        )}
      </span>
      <span className="shrink-0 text-xs text-ink-400">{lesson.estimatedMin} min</span>
    </>
  )

  const className =
    'flex min-h-15 w-full items-center gap-3 rounded-card bg-white px-4 py-3 text-left ring-1 ring-brand-900/5'

  if (locked) {
    return (
      <div className={`${className} opacity-60`} aria-disabled="true">
        {body}
      </div>
    )
  }

  return (
    <Link to={`/lesson/${lesson.id}`} className={`${className} hover:bg-sand-50`}>
      {body}
    </Link>
  )
}
